<template>
  <div class="live-tracker-wrapper" :class="isSmallScreen ? 'mobile' : ''">

    <div v-if="currentEvent" class="scoreboard-header">
      <div class="team-name team-home">
        {{ homeTeam.name }}
      </div>
      <div class="score-container">
        <span class="score-value">{{ homeScore }}</span>
        <span class="score-value">{{ awayScore }}</span>
      </div>
      <div class="team-name team-away">
        {{ awayTeam.name }}
      </div>

      <QuickSelectEventLiveAmerican v-if="isSmallScreen" :isSmallScreen="isSmallScreen" :label="homeTeam.name+' - '+ awayTeam.name" :id="currentEvent.id" />

    </div>

    <!-- Same panel as LiveMatchTracker (right column) and tradeTracker (mobile):
         our tracker, the TheSports widgets and the tracker data tabs. -->
    <MatchCenter
        v-if="currentEvent"
        :match-data="currentEvent"
        :event-id="currentEvent.id"
        :sport-id="currentEvent.sportId"
        :is-expanded="alwaysExpanded"
        :lock-expanded="true"
        :show-momentum="false"
        split-data-tabs
        @data-tabs="dataTabs = $event"
        @player-stats="playerStats = $event"
    />

    <div v-if="currentEvent" class="stats-section">
      <div class="stats-tabs-header centered-tabs">

        <div class="stats-nav">
          <div
              v-for="tab in bottomTabs"
              :key="'bottom-tab-' + tab._id"
              class="stats-tab"
              :class="{ active: currentBottomTab === tab._id }"
              @click="bottomTab = tab._id"
          >
            {{ tab.name }}
          </div>

        </div>

      </div>

      <div v-if="currentBottomTab === 'stats'" class="stats-content">

        <div v-if="isStatsLoading" class="stats-skeleton">
          <div class="sk-row">
            <div class="sk-circle-placeholder"></div>
            <div class="sk-circle-placeholder"></div>
            <div class="sk-circle-placeholder"></div>
          </div>
          <div class="sk-bar-group" v-for="n in 5" :key="n">
            <div class="sk-line sk-line--short"></div>
            <div class="sk-line sk-line--full"></div>
            <div class="sk-line sk-line--xs"></div>
          </div>
        </div>

        <template v-else>

          <div class="periods-table-container">
            <ScoreboardLiveAmerican
                :sport-id="currentEvent.sportId"
                :livescore="currentEvent.livescore"
                :competitors="{home: currentEvent.home, away: currentEvent.away}"
                :mode="'detail'"
                v-if="!isFootball"
            />
          </div>

          <div v-if="isTennis" class="tennis-stats-container">
            <div class="tennis-stat-row">
              <div class="side-stat">
                <div class="stat-val">{{ getStatVal(20, 1) }}</div>
                <div class="stat-divider home-fill"></div>
                <div class="stat-label">{{ $t('statistics.aces') }}</div>
              </div>
              <div class="center-stat">
                <div class="stat-values">
                  <span>{{ getStatVal(22, 1) }}</span>
                  <span>{{ getStatVal(22, 2) }}</span>
                </div>
                <div class="stat-bars">
                  <div class="bar-bg home-bar-bg">
                    <div class="bar-fill home-fill" :style="{ width: getStatWidth(22, 1) + '%' }"></div>
                  </div>
                  <div class="bar-bg away-bar-bg">
                    <div class="bar-fill away-fill" :style="{ width: getStatWidth(22, 2) + '%' }"></div>
                  </div>
                </div>
                <div class="stat-label">{{ $t('statistics.servicepoints') }}</div>
              </div>
              <div class="side-stat">
                <div class="stat-val">{{ getStatVal(20, 2) }}</div>
                <div class="stat-divider away-fill"></div>
                <div class="stat-label">{{ $t('statistics.aces') }}</div>
              </div>
            </div>

            <div class="tennis-stat-row">
              <div class="side-stat">
                <div class="stat-val">{{ getStatVal(21, 1) }}</div>
                <div class="stat-divider home-fill"></div>
                <div class="stat-label">{{ $t('statistics.doublefaults') }}</div>
              </div>
              <div class="center-stat">
                <div class="stat-values">
                  <span>{{ getStatVal(23, 1) }}</span>
                  <span>{{ getStatVal(23, 2) }}</span>
                </div>
                <div class="stat-bars">
                  <div class="bar-bg home-bar-bg">
                    <div class="bar-fill home-fill" :style="{ width: getStatWidth(23, 1) + '%' }"></div>
                  </div>
                  <div class="bar-bg away-bar-bg">
                    <div class="bar-fill away-fill" :style="{ width: getStatWidth(23, 2) + '%' }"></div>
                  </div>
                </div>
                <div class="stat-label">{{ $t('statistics.breakpoints') }}</div>
              </div>
              <div class="side-stat">
                <div class="stat-val">{{ getStatVal(21, 2) }}</div>
                <div class="stat-divider away-fill"></div>
                <div class="stat-label">{{ $t('statistics.doublefaults') }}</div>
              </div>
            </div>
          </div>

          <div v-else-if="isFootball" class="football-stats-container">
            <div class="fb-circles-row">
              <div class="fb-circle-item">
                <div class="fb-circle-label">{{ $t('statistics.attacks') }}</div>
                <div class="fb-circle-wrap">
                  <span class="fb-val-h">{{ footballData.attacks.h }}</span>
                  <div class="fb-donut" :style="{ background: `conic-gradient(from 180deg, var(--tracker-home-color) 0%, var(--tracker-home-color) ${footballData.attacks.pct}%, var(--tracker-away-color) ${footballData.attacks.pct}%, var(--tracker-away-color) 100%)` }">
                    <div class="fb-donut-inner"><v-icon dark small>mdi-chevron-right</v-icon></div>
                  </div>
                  <span class="fb-val-a">{{ footballData.attacks.a }}</span>
                </div>
              </div>
              <div class="fb-circle-item">
                <div class="fb-circle-label">{{ $t('statistics.dangerousattacks') }}</div>
                <div class="fb-circle-wrap">
                  <span class="fb-val-h">{{ footballData.dAttacks.h }}</span>
                  <div class="fb-donut" :style="{ background: `conic-gradient(from 180deg, var(--tracker-home-color) 0%, var(--tracker-home-color) ${footballData.dAttacks.pct}%, var(--tracker-away-color) ${footballData.dAttacks.pct}%, var(--tracker-away-color) 100%)` }">
                    <div class="fb-donut-inner"><v-icon dark small>mdi-chevron-double-right</v-icon></div>
                  </div>
                  <span class="fb-val-a">{{ footballData.dAttacks.a }}</span>
                </div>
              </div>
              <div class="fb-circle-item">
                <div class="fb-circle-label">{{ $t('statistics.possessionpct') }}</div>
                <div class="fb-circle-wrap">
                  <span class="fb-val-h">{{ footballData.poss.h }}</span>
                  <div class="fb-donut" :style="{ background: `conic-gradient(from 180deg, var(--tracker-home-color) 0%, var(--tracker-home-color) ${footballData.poss.pct}%, var(--tracker-away-color) ${footballData.poss.pct}%, var(--tracker-away-color) 100%)` }">
                    <div class="fb-donut-inner"><span class="fb-icon-text">%</span></div>
                  </div>
                  <span class="fb-val-a">{{ footballData.poss.a }}</span>
                </div>
              </div>
            </div>
            <div class="fb-bottom-row mt-6">
              <div class="fb-events-col">
                <div class="fb-events-icons">
                  <v-icon class="fb-flag-icon">mdi-flag</v-icon>
                  <div class="card-icon red-card"></div>
                  <div class="card-icon yellow-card"></div>
                </div>
                <div class="fb-events-values">
                  <span>{{ footballData.corners.h }}</span>
                  <span>{{ footballData.red.h }}</span>
                  <span>{{ footballData.yellow.h }}</span>
                </div>
              </div>
              <div class="fb-shots-col">
                <div class="fb-shots-label">{{ $t('statistics.shotsontargetlabel') }}</div>
                <div class="fb-shots-display">
                  <div class="fb-shots-text">{{ footballData.shots.h }}/{{ footballData.shotsOn.h }}</div>
                  <div class="fb-shots-bars-wrap">
                    <div class="stat-bars fb-s-bars">
                      <div class="bar-bg home-bar-bg">
                        <div class="bar-fill fb-blue-fill" :style="{ width: footballData.shots.hPct + '%' }"></div>
                      </div>
                      <div class="bar-bg away-bar-bg">
                        <div class="bar-fill fb-red-fill" :style="{ width: footballData.shots.aPct + '%' }"></div>
                      </div>
                    </div>
                    <div class="stat-bars fb-s-bars">
                      <div class="bar-bg home-bar-bg">
                        <div class="bar-fill fb-blue-fill" :style="{ width: footballData.shotsOn.hPct + '%' }"></div>
                      </div>
                      <div class="bar-bg away-bar-bg">
                        <div class="bar-fill fb-red-fill" :style="{ width: footballData.shotsOn.aPct + '%' }"></div>
                      </div>
                    </div>
                  </div>
                  <div class="fb-shots-text">{{ footballData.shots.a }}/{{ footballData.shotsOn.a }}</div>
                </div>
              </div>
              <div class="fb-events-col">
                <div class="fb-events-icons">
                  <div class="card-icon yellow-card"></div>
                  <div class="card-icon red-card"></div>
                  <v-icon class="fb-flag-icon">mdi-flag</v-icon>
                </div>
                <div class="fb-events-values">
                  <span>{{ footballData.yellow.a }}</span>
                  <span>{{ footballData.red.a }}</span>
                  <span>{{ footballData.corners.a }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="isBasket" class="basket-stats-container">

            <div class="bk-row bk-top-row">
              <div class="bk-side-stats">
                <div class="bk-stat-col">
                  <span class="bk-val">{{ basketData.fouls.h }}</span>
                  <div class="bk-line bk-home-line"></div>
                  <span class="bk-label">{{ $t('statistics.fouls') }}</span>
                </div>
                <div class="bk-stat-col">
                  <span class="bk-val">{{ basketData.pts2.h }}</span>
                  <div class="bk-line bk-home-line"></div>
                  <span class="bk-label">{{ $t('statistics.twopoints') }}</span>
                </div>
                <div class="bk-stat-col">
                  <span class="bk-val">{{ basketData.pts3.h }}</span>
                  <div class="bk-line bk-home-line"></div>
                  <span class="bk-label">{{ $t('statistics.threepoints') }}</span>
                </div>
              </div>

              <div class="bk-side-stats">
                <div class="bk-stat-col">
                  <span class="bk-val">{{ basketData.pts3.a }}</span>
                  <div class="bk-line bk-away-line"></div>
                  <span class="bk-label">{{ $t('statistics.threepoints') }}</span>
                </div>
                <div class="bk-stat-col">
                  <span class="bk-val">{{ basketData.pts2.a }}</span>
                  <div class="bk-line bk-away-line"></div>
                  <span class="bk-label">{{ $t('statistics.twopoints') }}</span>
                </div>
                <div class="bk-stat-col">
                  <span class="bk-val">{{ basketData.fouls.a }}</span>
                  <div class="bk-line bk-away-line"></div>
                  <span class="bk-label">{{ $t('statistics.fouls') }}</span>
                </div>
              </div>
            </div>

            <div class="bk-row bk-bottom-row mt-6">
              <div class="bk-timeouts">
                <div class="bk-dots">
                 <span
                     v-for="n in 5"
                     :key="'h-to-'+n"
                     class="bk-dot"
                     :class="{'filled bk-home-dot': n <= basketData.timeouts.h}">
                 </span>
                </div>
                <span class="bk-label">{{ $t('statistics.timeouts') }}</span>
              </div>

              <div class="bk-freethrows">
                <div class="bk-ft-values">
                  <span><strong class="text-white mr-1">{{ basketData.ft.h }}</strong> {{ basketData.ft.hPct }}%</span>
                  <span><strong class="text-white mr-1">{{ basketData.ft.a }}</strong> {{ basketData.ft.aPct }}%</span>
                </div>
                <div class="stat-bars mt-1 mb-1">
                  <div class="bar-bg home-bar-bg">
                    <div class="bar-fill bk-home-fill" :style="{ width: basketData.ft.hPct + '%' }"></div>
                  </div>
                  <div class="bar-bg away-bar-bg">
                    <div class="bar-fill bk-away-fill" :style="{ width: basketData.ft.aPct + '%' }"></div>
                  </div>
                </div>
                <span class="bk-label">{{ $t('statistics.freethrows') }}</span>
              </div>

              <div class="bk-timeouts">
                <div class="bk-dots">
                 <span
                     v-for="n in 5"
                     :key="'a-to-'+n"
                     class="bk-dot"
                     :class="{'filled bk-away-dot': n <= basketData.timeouts.a}">
                 </span>
                </div>
                <span class="bk-label">{{ $t('statistics.timeouts') }}</span>
              </div>
            </div>
          </div>

          <div v-else class="statistics-grid">
            <div v-for="(stat, index) in parsedStatistics" :key="'stat-'+index" class="stat-item">
              <div class="stat-values">
                <span class="val-home">{{ stat.homeValue }}</span>
                <span class="val-away">{{ stat.awayValue }}</span>
              </div>
              <div class="stat-bars">
                <div class="bar-bg home-bar-bg">
                  <div class="bar-fill home-fill" :style="{ width: stat.homePct + '%' }"></div>
                </div>
                <div class="bar-bg away-bar-bg">
                  <div class="bar-fill away-fill" :style="{ width: stat.awayPct + '%' }"></div>
                </div>
              </div>
              <div class="stat-label">{{ stat.label }}</div>
            </div>
          </div>

        </template>
      </div>

      <!-- Tracker data tabs, the same MatchCenter shows in its own toolbar. -->
      <div v-else-if="currentBottomTab === 'playerstats'" class="stats-content">
        <PlayerStatsSection :match-data="currentEvent" :player-stats="playerStats" />
      </div>
      <div v-else-if="currentBottomTab === 'timeline'" class="stats-content">
        <MatchTimeline :match-data="currentEvent" :player-stats="playerStats" />
      </div>
      <div v-else-if="currentBottomTab === 'lineups'" class="stats-content">
        <LineupSection :match-data="currentEvent" :player-stats="playerStats" />
      </div>
    </div>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import MatchCenter from "@/components/generic/MatchCenter.vue";
import { hasMatchTracker } from "@/services/matchtracker/matchTrackerSports";
import PlayerStatsSection from "@/components/center_widgets/Live/TradeTracker/PlayerStatsSection.vue";
import MatchTimeline from "@/components/center_widgets/Live/TradeTracker/MatchTimeline.vue";
import LineupSection from "@/components/center_widgets/Live/TradeTracker/LineUpSection.vue";
import ScoreboardLiveAmerican from "@/components/american/ScoreboardLiveAmerican.vue";
import QuickSelectEventLiveAmerican from "@/components/american/QuickSelectEventLiveAmerican.vue";

const ALL_STATS_DICTIONARY = {
  "0": "Not Set", "1": "Corners", "2": "Shots On Target", "3": "Shots Off Target", "4": "Attacks",
  "5": "Dangerous Attacks", "6": "Yellow Card", "7": "Red Card", "8": "Penalties", "9": "Goal",
  "10": "Substitutions", "11": "Possession", "12": "Fouls", "13": "Free Kicks", "14": "Goal Kicks",
  "15": "Offsides", "16": "Blocked Shots", "17": "Throw-ins", "18": "Woodwork Shots", "20": "Aces",
  "21": "Double Faults", "22": "Service Points", "23": "Break Points", "24": "Own Goal", "25": "Penalty Goal",
  "27": "Score", "28": "Two Points", "30": "Three Points", "31": "Time Outs", "32": "Free Throws",
  "33": "Hits", "34": "First Serve Wins", "36": "Wickets Taken", "38": "No Ball", "39": "Leg Bye",
  "40": "Missed Penalty", "47": "Longest Point Streak", "52": "Longest Aces Streak", "55": "Number Of Points",
  "56": "Last 10 Games Break Point Conversion", "59": "Five Game Streak In Row", "60": "Last Games Won To Love",
  "61": "Last Games Won Till 15", "62": "Last Games Broken Streak", "63": "Second Serve Return Points Won",
  "64": "Last 15 Points Won", "65": "Service Game Won", "66": "Break Points Converted", "67": "Break Points Saved",
  "68": "Second Serve Wins", "69": "First Serves In", "70": "First Serve Return Points Won", "71": "Return Games Won",
  "72": "Momentum", "77": "Number Of Serves", "78": "Return Games Played", "88": "Clearances", "93": "Runs",
  "96": "Goalkeeper Saves", "100": "Player Defensive Rebounds", "101": "Player Offensive Rebounds", "103": "Player Field Goals Made",
  "104": "Player Total Rebounds", "106": "Player Free Throws Made", "108": "Player 2-Point Field Goals Made",
  "109": "Player Steals", "110": "Player Free Throws Attempted", "111": "Player 3-Point Field Goals Attempted",
  "112": "Player Turnovers", "114": "Player 2-Point Field Goals Attempted", "117": "Goal Attempts", "122": "Total Yards",
  "123": "Passing Yards", "124": "Rushing Yards", "126": "Penalties Yards", "127": "Turnovers", "128": "Punts",
  "129": "Touchdowns", "130": "Rushing Touchdowns", "131": "Passing Touchdowns", "132": "Turnover Touchdowns",
  "133": "Interception Thrown", "134": "Fumbles Lost", "135": "Field Goals Succeeded", "136": "Field Goals Attempted",
  "137": "Net Yards", "138": "Red Zone Efficiency", "139": "Rush Attempts", "140": "Third Down Efficiency", "141": "Plays",
  "142": "Drive Field Goal Attempt", "143": "Drive First Down No", "144": "Drive First Down Yes", "145": "Drive Offensive Touch Down",
  "146": "Drive Punt", "147": "Drive Turnover", "148": "Pass Attempts", "149": "Pass Completions", "150": "Safeties",
  "151": "Two Point Conversions", "152": "Sacks Allowed", "153": "Extra Point", "154": "Minutes Batted", "155": "Overs Bowled",
  "156": "Boundary Sixes", "157": "Economy Rate", "159": "Type Runs Scored", "160": "Extras", "161": "No Balls", "162": "Bye",
  "163": "Maidens Bowled", "164": "Balls Bowled", "165": "Wide", "166": "Run Rate", "167": "Balls Faced", "169": "Runs Conceded",
  "170": "Boundary Fours", "176": "Batsman Fours", "178": "Batsman Sixes", "182": "Fours", "183": "Sixes",
  "1004": "Tower", "1005": "Dragon", "1007": "Baron", "1008": "Inhibitor", "1036": "Player Assists", "1042": "Pull Inbounds",
  "1043": "Pull Out Of Bounds", "1044": "Block", "1045": "Callahan", "1047": "Throwaway", "1048": "Throwaway caused",
  "1049": "Penalty against us while we have possession", "1050": "Penalty against us while they have possession",
  "1051": "Penalty against them while we have possession", "1052": "Penalty against them while they have possession",
  "1053": "Midpoint timeout called by us", "1054": "Midpoint timeout called by them", "1055": "Stall",
  "1056": "Throwaway caused via stall", "1057": "Drop", "1065": "Dropped", "1068": "Set defense subs",
  "1069": "Set offense subs", "1070": "Injury while we have possessions", "1071": "Injury while they have possessions",
  "1072": "Offsides against us", "1073": "Offsides against them", "1074": "VAR", "1075": "DLSMethod", "1076": "Fall of Wickets",
  "1078": "Partnership runs", "1079": "Partnership balls", "1080": "Tries", "1081": "Conversions",
  "2005": "Player Shots On Target", "2006": "Player Shots Off Target", "2008": "Points Won", "2009": "Receiver Points Won",
  "2010": "Service Errors", "2011": "Biggest Lead", "2012": "Number Of Lets", "2013": "Set Points", "2014": "Barracks",
  "2018": "Roshan", "2019": "Kills", "2029": "Rebounds", "2030": "Defensive Rebounds", "2031": "Offensive Rebounds",
  "2034": "Assists", "2035": "Steals", "2037": "2-Min Penalty", "2043": "Points Won", "2044": "Service Points Won",
  "2045": "Receiver Points Won", "2046": "Total Shots", "2047": "Power-Play Goals", "2048": "Face-offs Won",
  "2049": "Takeaways", "2050": "Giveaways", "2051": "Penalty Minutes", "2052": "Player Powerplay Goals",
  "2054": "Player Faceoff Won", "2055": "Faceoff Taken", "2060": "Gold", "2065": "Max Points In Row",
  "2066": "First serve percentage", "2067": "Receiver Points Percent", "2068": "Service Errors", "2078": "Deaths",
  "2080": "Doubles", "2081": "Triples", "2082": "Home Runs", "2083": "Strike Outs", "2084": "Base on balls",
  "2085": "At Bats", "2086": "RBI", "2087": "Outs", "2088": "Winning Face-Offs Percentage", "2090": "Max Games In Row",
  "2091": "Total Games Won Percentage", "2094": "Second Serve Percentage", "2095": "Break Points Won Percentage",
  "2096": "Errors", "2097": "Stolen Bases", "2100": "Fumbles", "2101": "Punts Average Yards", "2115": "Player Interceptions",
  "2147": "Total Games Won", "2148": "Crosses", "2156": "Free Throws Attempted", "2158": "Injuries", "2165": "Cloud Kills",
  "2166": "Infernal Kills", "2167": "Mountain KIlls", "2168": "Ocean Kills", "2169": "Chemtech Kills",
  "2170": "Hextech Kills", "2171": "Elder Kills", "2175": "Second Serves In", "2177": "Short Handed Goals",
  "2194": "Successful 1st Serves", "2195": "1st Serve Points Won (%)", "2196": "2nd Serve Points Won (%)",
  "2197": "Successful 2nd Serves", "2198": "1st Serve Return Points Won (%)", "2199": "2nd Serve Return Points Won (%)",
  "2200": "Service Points Won (%)", "2201": "Break Points Saved (%)", "2202": "Tie Breaks Won",
  "2205": "1st Serve Attempts", "2206": "2nd Serve Attempts", "2207": "1st Serve Points Played",
  "2208": "2nd Serve Points Played", "2209": "Break Points Attempts", "2215": "Free Throws Made (%)",
  "2216": "Field Goals Attempts", "2217": "Field Goals Made (%)", "2218": "Three Pointers Attempts",
  "2219": "Three Pointers Made (%)", "2220": "Two Pointers Attempts", "2221": "Two Pointers Made (%)",
  "2226": "Passing Accuracy (%)", "2228": "Tackles", "2229": "Service Games Played", "2230": "Service Games Won (%)",
  "2231": "1st Serve Return Points Played", "2232": "2nd Serve Return Points Played", "2233": "Return Points Played",
  "2240": "Comeback To Win", "2249": "Empty Net Goals", "2261": "Batting Average", "2262": "Strikes",
  "2263": "Hits Allowed by Pitchers", "2265": "Runs Allowed by Pitchers", "2272": "Home Runs Allowed by Pitchers",
  "2273": "Base on Balls Allowed by Pitchers", "2282": "5-Min Penalty", "2283": "Power play attempts",
  "2288": "Player Tackles", "2289": "Header Goals", "2310": "Dropped Goals", "2311": "Goal Percentage",
  "2312": "Metres Run With Ball", "2313": "Conversion Goal Attempts", "2314": "Total Runs", "2316": "Penalty Goal Attempts",
  "2318": "Left foot Goals", "2319": "Right Foot Goals", "2320": "Penalty Goals", "2321": "Injury",
  "2339": "Power-Play Percentage", "2341": "180s", "2344": "Woodwork Shots", "2346": "Receiving yards",
  "2347": "Two Point Conversion Attempts", "2348": "Extra Point Attempts", "2349": "Receiving Touchdowns",
  "2351": "Touchdowns by defensive/special team", "2352": "Extra Point Attempts (%)", "2370": "Goals Outside Penalty Area",
  "2371": "Left Foot Goal", "2374": "Goals From Outside Penalty Area", "2376": "Header Goals", "2380": "Crosses Made (%)",
  "2381": "Winners", "2382": "Unforced errors", "2388": "Free Kick Goals", "2389": "Penalties Saved",
  "2393": "Number of Icings", "2394": "Dangerous Free Kicks", "2395": "Field Goals Missed", "2398": "Balls",
  "2399": "Tackles Won (%)", "2400": "Interceptions", "2401": "Average Yards Per Play", "2402": "3rd Down Efficiency (%)",
  "2410": "Aces on 1st Serve", "2411": "Aces on 2nd Serve", "2412": "Aces on Break Point", "2413": "Points Served at Thirty All",
  "2414": "Points Won at Thirty All", "2415": "Points Served at Deuce", "2416": "Points Won at Deuce",
  "2417": "Deciding Points Played", "2418": "Deciding Points Won", "2419": "Deciding Points Lost", "2421": "Serve Points Played",
  "2422": "Match Points Opportunities", "2424": "WHIP", "2425": "On-base (%)", "2426": "Slugging (%)",
  "2428": "Shots inside the box", "2429": "Shots outside the box", "2430": "Successful Dribbles", "2433": "Fielding Errors",
  "2441": "Powerplay Goals", "2442": "Player Goals Inside Penalty Area", "2443": "Mercy", "2444": "Back to back hits",
  "2445": "Catchers Interference", "2446": "Hit By Pitch", "2474": "Medical Team", "2494": "FaceOff Won", "2495": "Faceoff Won (%)"
};

export default {
  name: 'LiveMatchTrackerAmerican',
  props: {
    isSmallScreen: {
      type: Boolean,
      default: false
    }
  },
  components: {QuickSelectEventLiveAmerican, ScoreboardLiveAmerican, MatchCenter, PlayerStatsSection, MatchTimeline, LineupSection },
  data() {
    return {
      eventSelected: null,
      bottomTab: "stats",
      // Tracker data tabs and player stats handed up by MatchCenter.
      dataTabs: [],
      playerStats: null,
    };
  },
  created() {
    // Set non-reattivo: traccia solo le richieste attualmente in volo
    this._fetchingIds = new Set();
  },
  computed: {
    ...mapGetters({
      'getEventsLiveDetail': 'detailLive/getEventsLiveDetail',
      'getPreviewLive': 'previewLive/getPreviewLive',
      'getViewMode': 'ui/getViewMode',
      'getLanguage': 'settings/getLanguage',
      'getRulesManager': 'rules/getRulesManager',
      'getRulesSkin': 'rules/getRulesSkin',
      'getViewLiveMatchTracker': 'odds/getViewLiveMatchTracker',
      'getUiPersonalizationGeneral': 'settings/getUiPersonalizationGeneral',
      'getCurrentPage': 'sportNavigation/currentPage',
    }),
    currentEvent() {
      if (this.getCurrentPage === 'live_detail') {
        return this.getEventsLiveDetail[0]?.event || null;
      }
      return this.getPreviewLive?.events?.[0]?.[0] || null;
    },
    isStatsLoading() {
      if (!this.currentEvent) return false;
      if (!this.currentEvent.livescore) return true;
      // In preview le statistiche non arrivano con il preview: aspettiamo la chiamata dedicata
      if (this.getCurrentPage !== 'live_detail') {
        return !Array.isArray(this.currentEvent.livescore.statistics);
      }
      return false;
    },

    homeTeam() {
      return this.currentEvent?.competitors?.find(c => c.position === "1") || { name: this.$t('home') };
    },
    awayTeam() {
      return this.currentEvent?.competitors?.find(c => c.position === "2") || { name: this.$t('statistics.away') };
    },
    homeScore() {
      return this.getGameScore(1);
    },
    awayScore() {
      return this.getGameScore(2);
    },
    currentGame() {
      if (!this.currentEvent.livescore || !this.currentEvent.livescore.periods || !Array.isArray(this.currentEvent.livescore.periods)) return null;
      return this.currentEvent.livescore.periods.find(p => p && p.type === '60') || null;
    },
    isTennis() {
      return String(this.currentEvent.sportId) === "54094";
    },
    isBasket() {
      return String(this.currentEvent.sportId) === "48242";
    },
    isFootball() {
      return String(this.currentEvent.sportId) === "6046";
    },

    footballData() {
      const getV = (id, pos) => parseFloat(this.getStatVal(id, pos)) || 0;

      const attacks = { h: getV(4, 1), a: getV(4, 2) };
      const dAttacks = { h: getV(5, 1), a: getV(5, 2) };
      const poss = { h: getV(11, 1), a: getV(11, 2) };

      const sOnH = getV(2, 1);
      const sOnA = getV(2, 2);
      const sOffH = getV(3, 1);
      const sOffA = getV(3, 2);
      const shotsH = sOnH + sOffH;
      const shotsA = sOnA + sOffA;

      const corners = { h: getV(1, 1), a: getV(1, 2) };
      const yellow = { h: getV(6, 1), a: getV(6, 2) };
      const red = { h: getV(7, 1), a: getV(7, 2) };

      const calcPctH = (h, a) => (h + a === 0) ? 50 : (h / (h + a)) * 100;
      const calcPctA = (h, a) => (h + a === 0) ? 50 : (a / (h + a)) * 100;

      return {
        attacks: { ...attacks, pct: calcPctH(attacks.h, attacks.a) },
        dAttacks: { ...dAttacks, pct: calcPctH(dAttacks.h, dAttacks.a) },
        poss: { ...poss, pct: (poss.h + poss.a === 0 && poss.h === 0) ? 50 : (poss.h || 50) },
        shots: { h: shotsH, a: shotsA, hPct: calcPctH(shotsH, shotsA), aPct: calcPctA(shotsH, shotsA) },
        shotsOn: { h: sOnH, a: sOnA, hPct: calcPctH(sOnH, sOnA), aPct: calcPctA(sOnH, sOnA) },
        corners, yellow, red
      };
    },

    basketData() {
      const getV = (id, pos) => parseFloat(this.getStatVal(id, pos)) || 0;

      const fouls = { h: getV(12, 1), a: getV(12, 2) };
      const pts2 = { h: getV(28, 1), a: getV(28, 2) };
      const pts3 = { h: getV(30, 1), a: getV(30, 2) };
      const timeouts = { h: getV(31, 1), a: getV(31, 2) };
      const ft = { h: getV(32, 1), a: getV(32, 2) };

      const calcPctH = (h, a) => (h + a === 0) ? 50 : Math.round((h / (h + a)) * 100);
      const calcPctA = (h, a) => (h + a === 0) ? 50 : Math.round((a / (h + a)) * 100);

      return {
        fouls,
        pts2,
        pts3,
        timeouts,
        ft: { ...ft, hPct: calcPctH(ft.h, ft.a), aPct: calcPctA(ft.h, ft.a) }
      };
    },

    parsedStatistics() {
      const rawStats = this.currentEvent?.livescore?.statistics || [];

      const statLabelsLocalOverride = {
        "1": this.$t('statistics.corners'),
        "2": this.$t('statistics.shotsontarget'),
        "3": this.$t('statistics.shotsofftarget'),
        "4": this.$t('statistics.attacks'),
        "5": this.$t('statistics.dangerousattacks'),
        "6": this.$t('statistics.yellow'),
        "7": this.$t('statistics.redcard'),
        "11": this.$t('statistics.possession'),
        "13": this.$t('statistics.freekicks'),
        "14": this.$t('statistics.goalkicks'),
        "17": this.$t('statistics.throwins')
      };

      return rawStats.map(stat => {
        const homeVal = parseFloat(stat.results?.find(r => r.position === "1")?.value || 0);
        const awayVal = parseFloat(stat.results?.find(r => r.position === "2")?.value || 0);
        const total = homeVal + awayVal;

        let homePct = 50; let awayPct = 50;
        if (total > 0) {
          homePct = (homeVal / total) * 100;
          awayPct = (awayVal / total) * 100;
        }

        let assignedLabel = statLabelsLocalOverride[stat.type];

        if (!assignedLabel || assignedLabel.startsWith('statistics.')) {
          assignedLabel = ALL_STATS_DICTIONARY[stat.type] || `Stat ${stat.type}`;
        }

        return {
          type: stat.type,
          label: assignedLabel,
          homeValue: homeVal,
          awayValue: awayVal,
          homePct: homePct,
          awayPct: awayPct
        };
      }).filter(stat => stat.homeValue > 0 || stat.awayValue > 0);
    },
    // Our stats first, then MatchCenter's data tabs (its own stats tab is
    // replaced by ours).
    bottomTabs() {
      return [{ _id: 'stats', name: this.$t('stats') }]
          .concat(this.dataTabs.filter(t => t._id !== 'stats'));
    },
    // The picked tab, or stats when it is gone (event without lineups...).
    currentBottomTab() {
      return this.bottomTabs.some(t => t._id === this.bottomTab) ? this.bottomTab : 'stats';
    },
    // Live detail on desktop with our MatchTracker: the right column stays at
    // the tracker's fullscreen width (see TemplateMarketView). The size is
    // always locked here: no fullscreen button in the American module.
    alwaysExpanded() {
      return !this.isSmallScreen &&
          this.getCurrentPage === 'live_detail' &&
          !!this.currentEvent &&
          hasMatchTracker(this.currentEvent.sportId, this.getUiPersonalizationGeneral);
    },
  },
  methods: {
    ...mapActions({
      'getEventIdLiveTracker': 'odds/getEventIdLiveTracker',
      'setViewLiveMatchTracker': 'odds/setViewLiveMatchTracker',
      'fetchStatisticsForPreviewEvent': 'previewLive/fetchStatisticsForPreviewEvent',
    }),
    getGameScore(position) {
      if (this.isTennis) {
        if (!this.currentGame || !this.currentGame.results || !Array.isArray(this.currentGame.results)) return '0';
        const res = this.currentGame.results.find(r => r && String(r.position) === String(position));
        return res ? res.value : '0';
      }

      if (this.isBasket) {
        if (!this.currentEvent?.livescore?.scoreboard?.results || !Array.isArray(this.currentEvent.livescore.scoreboard.results)) return '0';
        const res = this.currentEvent.livescore.scoreboard.results.find(r => r && String(r.position) === String(position));
        return res ? res.value : '0';
      }

      return this.currentEvent?.livescore?.scoreboard?.results?.find(r => r.position === position.toString())?.value || "0";
    },

    _maybeFetchStats(event) {
      if (!event || this.getCurrentPage === 'live_detail') return;
      if (Array.isArray(event?.livescore?.statistics)) return;
      // Evita richieste duplicate concorrenti sullo stesso evento
      if (this._fetchingIds.has(event.id)) return;
      this._fetchingIds.add(event.id);
      this.fetchStatisticsForPreviewEvent({
        eventId: event.id,
        sportId: event.sportId,
        tournamentId: event.tournamentId,
      }).finally(() => {
        this._fetchingIds.delete(event.id);
      });
    },
    getStatVal(typeId, position) {
      const rawStats = this.currentEvent?.livescore?.statistics || [];
      const stat = rawStats.find(s => String(s.type) === String(typeId));
      if (!stat) return "0";
      const res = stat.results?.find(r => String(r.position) === String(position));
      return res ? res.value : "0";
    },
    getStatWidth(typeId, position) {
      const val1 = parseFloat(this.getStatVal(typeId, 1)) || 0;
      const val2 = parseFloat(this.getStatVal(typeId, 2)) || 0;
      const total = val1 + val2;

      if (total === 0) return 50;
      const val = position === 1 ? val1 : val2;
      return (val / total) * 100;
    }
  },
  watch: {
    currentEvent: {
      immediate: true,
      handler(newVal, oldVal) {
        this._maybeFetchStats(newVal);
        if (newVal?.id !== oldVal?.id) this.bottomTab = 'stats';
      }
    },
  },
};
</script>

<style scoped>
.live-tracker-wrapper {
  background-color: var(--bg-body);
  color: var(--text-platform);
  overflow: hidden;
  font-family: Arial, sans-serif;
  border-left: 1px solid var(--hsla-1);
  min-height: calc(100vh - 80px);
}

.live-tracker-wrapper.mobile{
  border-left: none;
  min-height: auto;

}

.scoreboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 50px;
}
.team-name {
  flex: 1;
  font-size: var(--font-size-3xl);
  font-weight: 600;
  color: var(--text-platform);
  display: flex;
  justify-content: center;
}
.team-home { text-align: right; padding-right: 15px;justify-content: flex-end; }
.team-away { text-align: left; padding-left: 15px; justify-content: flex-start; }

.score-container {
  display: flex;
  flex: 0 1 auto;
  font-size: var(--font-size-9xl);
  font-weight: 900;
  color: var(--tracker-score-color);
}
.score-value {
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 0 5px;
  flex-direction: column;
  width: 50%;
}

.stats-section {
  border-top: 1px solid var(--hsla-1);
}
/* --- BARRA UNIFICATA --- */
.stats-tabs-header {
  display: flex;
  justify-content: space-between; /* Separa i tab di sinistra dalle icone di destra */
  align-items: center;
  border-bottom: 1px solid var(--hsla-1);
  padding: 0 15px; /* Aggiunge respiro ai lati */
}
.stats-tabs-header.centered-tabs {
  justify-content: center;
}

/* Contenitore sinistro (Stats/Summary) */
.stats-nav {
  display: flex;
  align-items: center;
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
}
.stats-nav::-webkit-scrollbar { display: none; }
.stats-nav .stats-tab { white-space: nowrap; flex-shrink: 0; }

.stats-tab {
  padding: 12px 15px; /* Leggermente ridotto per adattarsi meglio sulla stessa riga */
  color: var(--text-op-50-platform);
  font-size: var(--font-size-4xl);
  font-weight: 500;
  cursor: pointer;
  position: relative;
}

.stats-tab.active {
  color: var(--text-platform);
  font-weight: bold;
}

.stats-tab.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 10%;
  width: 80%; /* Allargato leggermente per estetica */
  height: 2px;
  background-color: var(--text-platform);
}

.stats-tab {
  padding: 12px 20px;
  color: var(--text-op-50-platform);
  font-size: var(--font-size-4xl);
  font-weight: 500;
  cursor: pointer;
  position: relative;
}
.stats-tab.active {
  color: var(--text-platform);
  font-weight: bold;
}
.stats-tab.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 20%;
  width: 60%;
  height: 2px;
  background-color: var(--text-platform);
}
.stats-content {
  padding: 20px;
}
.mobile .stats-content {
  padding: 0 0 20px 0;
}
.periods-table-container {
  margin-bottom: 25px;
}

.football-stats-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.fb-circles-row {
  display: flex;
  justify-content: space-around;
  align-items: center;
}
.fb-circle-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.fb-circle-label { font-size: var(--font-size-3xl); color: var(--text-op-50-platform); }
.fb-circle-wrap { display: flex; align-items: center; gap: 15px; }
.fb-val-h, .fb-val-a { font-size: var(--font-size-7xl); font-weight: bold; color: var(--text-platform); }
.fb-donut { width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
.fb-donut-inner { width: 32px; height: 32px; background-color: var(--tracker-donut-inner-bg); border-radius: 50%; display: flex; align-items: center; justify-content: center; }
.fb-icon-text { font-size: var(--font-size-5xl); font-weight: bold; color: var(--text-platform); }
.fb-bottom-row { display: flex; justify-content: space-between; align-items: flex-end; padding: 0 5px; }
.fb-events-col { display: flex; flex-direction: column; gap: 8px; }
.fb-events-icons, .fb-events-values { display: flex; gap: 12px; align-items: center; justify-content: center; }
.fb-events-values span { font-size: var(--font-size-4xl); font-weight: bold; width: 16px; text-align: center; color: var(--text-platform); }
.card-icon { width: 12px; height: 16px; border-radius: 2px; }
.red-card { background-color: var(--tracker-away-color); }
.yellow-card { background-color: var(--tracker-home-accent); }
.fb-flag-icon { font-size: var(--font-size-6xl) !important; color: var(--tracker-away-color) !important; }
.fb-shots-col { display: flex; flex-direction: column; align-items: center; flex: 1; margin: 0 25px; gap: 8px; }
.fb-shots-label { font-size: var(--font-size-2xl); color: var(--tracker-label-color); }
.fb-shots-display { display: flex; align-items: center; width: 100%; gap: 15px; }
.fb-shots-text { font-size: var(--font-size-5xl); font-weight: bold; color: var(--text-platform); white-space: nowrap; }
.fb-shots-bars-wrap { flex: 1; display: flex; flex-direction: column; gap: 6px; }
.fb-s-bars { margin-bottom: 0 !important; }
.fb-blue-fill { background-color: var(--tracker-home-color); }
.fb-red-fill { background-color: var(--tracker-away-color); }

.tennis-stats-container { display: flex; flex-direction: column; }
.tennis-stat-row { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 25px; width: 100%; }
.side-stat { flex: 0 0 25%; display: flex; flex-direction: column; align-items: center; }
.side-stat .stat-val { font-size: var(--font-size-6xl); font-weight: bold; color: var(--text-platform); margin-bottom: 6px; }
.side-stat .stat-divider { width: 45px; height: 3px; margin-bottom: 6px; border-radius: 2px; }
.center-stat { flex: 0 0 45%; display: flex; flex-direction: column; }
.center-stat .stat-values { display: flex; justify-content: space-between; font-size: var(--font-size-6xl); font-weight: bold; color: var(--text-platform); margin-bottom: 6px; }

.basket-stats-container {
  display: flex;
  flex-direction: column;
}
.bk-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
.bk-side-stats {
  display: flex;
  gap: 20px;
}
.bk-stat-col {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.bk-val {
  font-size: var(--font-size-7xl);
  font-weight: bold;
  color: var(--text-platform);
  margin-bottom: 5px;
}
.bk-line {
  width: 35px;
  height: 3px;
  border-radius: 2px;
  margin-bottom: 5px;
}
.bk-home-line { background-color: var(--tracker-home-accent); }
.bk-away-line { background-color: var(--text-platform); }

.bk-label {
  font-size: var(--font-size-2xl);
  color: var(--tracker-label-color);
}

.bk-bottom-row {
  align-items: flex-end;
}

.bk-timeouts {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.bk-dots {
  display: flex;
  gap: 4px;
}
.bk-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: var(--tracker-bar-bg);
}
.bk-dot.filled.bk-home-dot { background-color: var(--tracker-home-accent); }
.bk-dot.filled.bk-away-dot { background-color: var(--text-platform); }

.bk-freethrows {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 40px;
}
.bk-ft-values {
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-size: var(--font-size-4xl);
  color: var(--tracker-label-color);
}
.bk-home-fill { background-color: var(--tracker-home-accent); }
.bk-away-fill { background-color: var(--text-platform); }

.statistics-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px 30px; }
.stat-item { display: flex; flex-direction: column; }
.stat-values { display: flex; justify-content: space-between; font-size: var(--font-size-6xl); font-weight: bold; margin-bottom: 4px; }
.stat-bars { display: flex; gap: 4px; height: 3px; margin-bottom: 6px; width: 100%; }
.bar-bg { flex: 1; background-color: var(--tracker-bar-bg); border-radius: 2px; overflow: hidden; position: relative; }
.home-bar-bg { direction: rtl; }
.away-bar-bg { direction: ltr; }
.bar-fill { height: 100%; }
.home-fill { background-color: var(--tracker-home-fill); }
.away-fill { background-color: var(--tracker-away-fill); }
.stat-label { text-align: center; color: var(--tracker-label-color); font-size: var(--font-size-2xl); margin-top: 2px; }

@keyframes sk-shimmer {
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
}
.stats-skeleton {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.sk-row {
  display: flex;
  justify-content: space-around;
  gap: 12px;
}
.sk-circle-placeholder {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(90deg, var(--tracker-skeleton-base) 25%, var(--tracker-skeleton-shine) 50%, var(--tracker-skeleton-base) 75%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.4s infinite linear;
}
.sk-bar-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sk-line {
  border-radius: 4px;
  background: linear-gradient(90deg, var(--tracker-skeleton-base) 25%, var(--tracker-skeleton-shine) 50%, var(--tracker-skeleton-base) 75%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.4s infinite linear;
  height: 8px;
}
.sk-line--full  { width: 100%; height: 5px; }
.sk-line--short { width: 40%; align-self: center; }
.sk-line--xs    { width: 25%; height: 6px; align-self: center; }


@media (max-width: 600px) {
  .team-name {
    font-size: var(--font-size-2xl) !important;
    line-height: 1.4;
  }
  .stats-tab{
    font-size: var(--font-size-2xl) !important;
  }
  .fb-val-h, .fb-val-a{
    font-size: var(--font-size-6xl) !important;

  }
}
</style>